/**
 * sheetOps — pure helpers for structural grid edits (insert/delete rows &
 * columns) and clipboard serialization. Operates on a sheet's {cells, formats}
 * address maps. Keeps logic out of the React component and unit-testable.
 *
 * Note: these shift cell CONTENT and formats by address. Formula references
 * inside moved cells are not rewritten (a known v1 limitation).
 */
import { parseRef, cellName } from "./sheetEngine";

// Rebuild an address->value map, transforming each address's {row,col}. `fn`
// returns a new {row,col} or null to drop the entry.
const remap = (map, fn) => {
  const out = {};
  Object.keys(map).forEach((addr) => {
    const p = parseRef(addr);
    if (!p) return;
    const np = fn(p);
    if (np && np.row >= 0 && np.col >= 0) out[cellName(np.row, np.col)] = map[addr];
  });
  return out;
};

const shiftBoth = (sheet, fn) => ({
  cells: remap(sheet.cells, fn),
  formats: remap(sheet.formats, fn),
});

// Returns { cells, formats } for the sheet after the structural edit.
export const insertRow = (sheet, at) => shiftBoth(sheet, (p) => ({ row: p.row >= at ? p.row + 1 : p.row, col: p.col }));
export const deleteRow = (sheet, at) =>
  shiftBoth(sheet, (p) => (p.row === at ? null : { row: p.row > at ? p.row - 1 : p.row, col: p.col }));
export const insertCol = (sheet, at) => shiftBoth(sheet, (p) => ({ row: p.row, col: p.col >= at ? p.col + 1 : p.col }));
export const deleteCol = (sheet, at) =>
  shiftBoth(sheet, (p) => (p.col === at ? null : { row: p.row, col: p.col > at ? p.col - 1 : p.col }));

// ── clipboard (TSV) ──────────────────────────────────────────────────────────
// Build a tab/newline TSV string from a 2D array of strings (Excel-compatible).
export const toTSV = (grid2d) =>
  grid2d
    .map((row) => row.map((cell) => (/[\t\n"]/.test(cell) ? `"${String(cell).replace(/"/g, '""')}"` : cell)).join("\t"))
    .join("\n");

// Parse a pasted TSV/CSV-ish clipboard string into a 2D array of strings.
export const fromTSV = (text) => {
  const str = String(text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  if (str === "") return [[""]];
  // Simple split (no embedded-newline-in-quotes support — adequate for v1 paste).
  return str.replace(/\n$/, "").split("\n").map((line) => line.split("\t"));
};
